import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { examBoards } from '@/content/examResources';

/**
 * /exam-corner — a curated hub that links OUT to official, free exam resources
 * (NCERT/CBSE + Kerala SCERT/DHSE). We never host the material; we point to the
 * source. Original guidance copy + honest "official, verify the session" notes.
 */
export function ExamCornerPage() {
  return (
    <div>
      <SEO
        title="Exam Corner — Free Official Notes, Syllabus & Question Papers (CBSE & Kerala) | ACES"
        description="A curated guide to free, official Plus One & Plus Two commerce resources: NCERT & SCERT textbooks, CBSE & Kerala syllabus, sample papers, marking schemes, previous-year papers and focus areas — straight from the official sources."
        canonical="https://acescommerce.com/exam-corner/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Exam Corner — official commerce exam resources',
          description: 'Curated links to official free CBSE/NCERT and Kerala SCERT/DHSE resources for Class 11 & 12 commerce.',
          url: 'https://acescommerce.com/exam-corner/',
        }}
      />

      {/* Hero */}
      <section className="aces-hero">
        <div className="max-w-5xl mx-auto px-4 pt-16 sm:pt-20 pb-12">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-accent-600 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" aria-hidden="true" />
            Exam Corner
          </span>
          <h1 className="font-serif text-[2.4rem] sm:text-5xl lg:text-[3.4rem] text-ink-900 tracking-tight leading-[1.06] max-w-2xl text-balance">
            Free official resources, <em className="italic text-accent-600">all in one place</em>
          </h1>
          <p className="mt-5 text-lg text-ink-600 max-w-2xl leading-relaxed">
            Textbooks, syllabus, sample papers and previous-year papers for Plus One &amp; Plus Two
            commerce — we’ve gathered the genuine, free, official links so you don’t have to hunt for them.
          </p>
          <div className="mt-7 rounded-card border border-ink-200/80 border-l-4 border-l-accent-500 bg-cream/90 px-5 py-4 text-sm text-ink-600 max-w-2xl leading-relaxed shadow-soft">
            Every link below opens the <strong className="text-ink-800">official government / board site</strong> —
            free and first-hand. ACES doesn’t host or sell these; we just point you to the source. Links open in a
            new tab, and because boards refresh papers each year, always confirm the current exam session on the
            official page.
          </div>
        </div>
      </section>

      {/* Boards */}
      {examBoards.map((board) => (
        <section key={board.id} className="bg-ink-50 border-t border-ink-200">
          <div className="max-w-5xl mx-auto px-4 py-14">
            <h2 className="font-serif text-[1.75rem] md:text-[2.1rem] text-ink-900 tracking-tight">{board.label}</h2>
            <p className="text-ink-500 mt-2 text-sm max-w-2xl leading-relaxed">{board.blurb}</p>
            <div className="mt-7 grid gap-4 md:grid-cols-2">
              {board.resources.map((r) => (
                <a
                  key={r.title}
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group aces-card-hover rounded-card border border-ink-200/80 bg-cream p-5 sm:p-6 shadow-card hover:border-accent-500/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-600 focus-visible:ring-offset-2"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-accent-600">{r.category}</span>
                    <span className="text-[10px] text-ink-500">{r.appliesTo}</span>
                  </div>
                  <h3 className="font-serif text-lg text-ink-900 mt-2 leading-snug transition-colors duration-150 group-hover:text-accent-700">{r.title}</h3>
                  <p className="text-sm text-ink-600 mt-2 leading-relaxed">{r.description}</p>
                  <div className="mt-3 flex items-center gap-1.5 text-sm font-medium text-ink-700 group-hover:text-accent-600 transition-colors duration-150">
                    <span>Open on {r.source}</span>
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" className="w-3.5 h-3.5" aria-hidden="true">
                      <path d="M5 11L11 5M6 5h5v5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  {r.note && <p className="mt-2 text-[11px] text-ink-500 italic leading-snug">{r.note}</p>}
                </a>
              ))}
            </div>
            {board.footnote && (
              <p className="mt-6 text-xs text-ink-600 leading-relaxed max-w-2xl rounded-card bg-ink-100/60 border border-ink-200/80 px-4 py-3">
                {board.footnote}
              </p>
            )}
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="aces-band-night bg-night-700">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-white tracking-tight">Read the official book — then make it stick</h2>
          <p className="text-ink-100/80 mt-3 text-sm max-w-md mx-auto leading-relaxed">
            Official papers tell you what to study. Our free chapter notes, worked examples and quizzes
            help you actually understand it.
          </p>
          <Link
            to="/"
            className="aces-btn-primary mt-7"
          >
            Open free chapter notes →
          </Link>
        </div>
      </section>
    </div>
  );
}
